import React, { useState, useRef, useEffect } from 'react';
import { MultiSelectFilter as MultiSelectFilterType } from '../types';
import { useFilter } from '../FilterContext';
import { cn } from '../../../../lib/utils';

import { useTranslation } from 'react-i18next';

interface MultiSelectFilterProps {
  filter: MultiSelectFilterType;
  className?: string;
}

export const MultiSelectFilter: React.FC<MultiSelectFilterProps> = ({
  filter,
  className,
}) => {
  const { t } = useTranslation('pleri/dashboard');
  const { value = [], update, isActive } = useFilter(filter.id);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToggleOption = (optionValue: string | number) => {
    const currentValues = value as (string | number)[];
    const isSelected = currentValues.includes(optionValue);

    let newValues: (string | number)[];
    if (isSelected) {
      newValues = currentValues.filter((v) => v !== optionValue);
    } else {
      // Check max selections limit
      if (
        filter.maxSelections &&
        currentValues.length >= filter.maxSelections
      ) {
        return; // Don't add more if limit reached
      }
      newValues = [...currentValues, optionValue];
    }

    update(newValues);
  };

  const handleClear = () => {
    update([]);
  };

  const getSelectedLabels = () => {
    const currentValues = value as (string | number)[];
    return filter.options
      .filter((option) => currentValues.includes(option.value))
      .map((option) => option.label);
  };

  const selectedLabels = getSelectedLabels();
  const displayText =
    selectedLabels.length > 0
      ? selectedLabels.join(', ')
      : filter.placeholder || t('selectOptions');

  return (
    <div className={cn('space-y-2', className)}>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
        {filter.label}
        {filter.required && <span className="ml-1 text-red-500">*</span>}
        {filter.maxSelections && (
          <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
            {t('max', { maxSelections: filter.maxSelections })}
          </span>
        )}
      </label>
      <div className="relative" ref={dropdownRef}>
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className={cn(
            'w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm dark:border-gray-600',
            'focus:border-[#FF732E] focus:ring-2 focus:ring-[#FF732E] focus:outline-none',
            'bg-white text-left text-sm dark:bg-gray-900',
            'flex items-center justify-between',
            isActive && 'border-[#FF732E] bg-orange-50 dark:bg-gray-800',
            isOpen && 'border-[#FF732E] ring-2 ring-[#FF732E]',
          )}
        >
          <span
            className={cn(
              'truncate',
              selectedLabels.length === 0 && 'text-gray-500 dark:text-gray-400',
            )}
          >
            {displayText}
          </span>
          <div className="flex items-center space-x-1">
            {selectedLabels.length > 0 && filter.allowClear && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleClear();
                }}
                className="p-1 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-400"
              >
                <svg
                  className="h-3 w-3"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            )}
            <svg
              className={cn(
                'h-4 w-4 text-gray-400 transition-transform dark:text-gray-500',
                isOpen && 'rotate-180',
              )}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </div>
        </button>

        {isOpen && (
          <div className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md border border-gray-300 bg-white shadow-lg dark:border-gray-600 dark:bg-gray-900">
            {filter.options.map((option) => {
              const isSelected = (value as (string | number)[]).includes(
                option.value,
              );
              const isDisabled =
                option.disabled ||
                (filter.maxSelections &&
                  !isSelected &&
                  (value as (string | number)[]).length >=
                    filter.maxSelections);

              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handleToggleOption(option.value)}
                  disabled={!!isDisabled}
                  className={cn(
                    'w-full px-3 py-2 text-left text-sm',
                    'hover:bg-gray-50 focus:bg-gray-50 focus:outline-none dark:hover:bg-gray-800',
                    'flex items-center space-x-2',
                    isSelected &&
                      'bg-orange-50 text-[#FF732E] dark:bg-gray-800',
                    isDisabled && 'cursor-not-allowed opacity-50',
                  )}
                >
                  <div
                    className={cn(
                      'flex h-4 w-4 items-center justify-center rounded border',
                      isSelected
                        ? 'border-[#FF732E] bg-[#FF732E]'
                        : 'border-gray-300 dark:border-gray-600',
                    )}
                  >
                    {isSelected && (
                      <svg
                        className="h-3 w-3 text-white"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    )}
                  </div>
                  <span>{option.label}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>
      {selectedLabels.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {selectedLabels.map((label, index) => (
            <span
              key={index}
              className="inline-flex items-center rounded-full bg-orange-100 px-2 py-1 text-xs text-[#FF732E] dark:bg-gray-700"
            >
              {label}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};
